import { useEffect, useRef, useState } from "react";
import { GESTURE_EVENTS } from "../../config/globals";

const DEFAULT_FADE_DELAY = 8000;

interface UseFadeOptions {
	/**
	 * Time in milliseconds before element fade out
	 */
	fadeDelay?: number;
	/**
	 * Events that should trigger element visibility and reset the timer
	 */
	interactionEvents?: string[];
}

interface UseFadeReturn {
	/**
	 * element is faded out (i.e. not visible)
	 */
	isFaded: boolean;
	/**
	 * Style object to apply to the element for fade behavior
	 */
	fadeStyles: React.CSSProperties;
}

/**
 * Custom hook that manages element fade in/out behavior
 * Returns visibility state and style properties to apply to the element.
 *
 * @param options - Configuration options for the fade behavior
 * @returns Object containing isVisible state and style properties
 */
export function useFade(options: UseFadeOptions = {}): UseFadeReturn {
	const { fadeDelay = DEFAULT_FADE_DELAY, interactionEvents = GESTURE_EVENTS } = options;

	const [isFaded, setIsFaded] = useState(false);
	const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(null);

	useEffect(() => {
		const resetTimer = () => {
			if (hideTimerRef.current) {
				clearTimeout(hideTimerRef.current);
			}

			setIsFaded(false);

			hideTimerRef.current = setTimeout(() => {
				setIsFaded(true);
			}, fadeDelay);
		};

		interactionEvents.forEach((eventType) => {
			window.addEventListener(eventType, resetTimer, { passive: true });
		});

		resetTimer();

		return () => {
			interactionEvents.forEach((event) => {
				window.removeEventListener(event, resetTimer);
			});
			if (hideTimerRef.current) {
				clearTimeout(hideTimerRef.current);
			}
		};
	}, [fadeDelay, interactionEvents]);

	// Only fade out, styling will pop in to feel more responsive
	const fadeStyles: React.CSSProperties = {
		opacity: isFaded ? 0 : 1,
		transition: isFaded ? "opacity 1s ease-in-out" : "none",
	};

	return { isFaded, fadeStyles };
}
